import { motion } from "framer-motion";
import CTABanner from "@/components/CTABanner";
import SEO from "@/components/SEO";

export default function Privacy() {
  return (
    <div>
      <SEO 
        title="Privacy Policy"
        description="Privacy Policy for Aussie Inspections. Learn how we collect, use, and protect your personal information."
        url="/privacy"
      />

      <CTABanner />

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-chivo text-4xl md:text-5xl font-black text-slate-900 mb-6">
              Privacy Policy
            </h1>
            <p className="text-slate-600 mb-8">
              Last updated: December 2024
            </p>

            <div className="prose prose-lg prose-slate max-w-none">
              <h2>Introduction</h2>
              <p>
                Aussie Inspections ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
              </p>

              <h2>Information We Collect</h2>
              <h3>Personal Information</h3>
              <p>We may collect personal information that you voluntarily provide to us when you:</p>
              <ul>
                <li>Request a quote or book an inspection</li>
                <li>Fill out a contact form</li>
                <li>Subscribe to our newsletter or blog updates</li>
                <li>Communicate with us via phone or email</li>
              </ul>
              <p>This information may include:</p>
              <ul>
                <li>Name</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Property address</li>
                <li>Service requirements</li>
              </ul>

              <h3>Automatic Information</h3>
              <p>When you visit our website, we may automatically collect certain information about your device, including:</p>
              <ul>
                <li>IP address</li>
                <li>Browser type</li>
                <li>Operating system</li>
                <li>Pages visited and time spent on pages</li>
                <li>Referring website addresses</li>
              </ul>

              <h2>How We Use Your Information</h2>
              <p>We use the information we collect to:</p>
              <ul>
                <li>Provide, maintain, and improve our inspection services</li>
                <li>Process and fulfill your service requests</li>
                <li>Send you quotes, confirmations, and inspection reports</li>
                <li>Respond to your comments, questions, and requests</li>
                <li>Send you technical notices, updates, and administrative messages</li>
                <li>Communicate with you about services, offers, and events</li>
                <li>Monitor and analyze trends, usage, and activities</li>
                <li>Detect, prevent, and address technical issues</li>
              </ul>

              <h2>How We Share Your Information</h2>
              <p>We do not sell, trade, or rent your personal information to third parties. We may share your information in the following situations:</p>
              <ul>
                <li><strong>Service Providers:</strong> We may share your information with third party service providers who perform services on our behalf, such as email delivery, customer relationship management, and website analytics.</li>
                <li><strong>Legal Requirements:</strong> We may disclose your information if required to do so by law or in response to valid requests by public authorities.</li>
                <li><strong>Business Transfers:</strong> We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business.</li>
              </ul>

              <h2>Data Security</h2>
              <p>
                We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
              </p>

              <h2>Data Retention</h2>
              <p>
                We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer need your information, we will securely delete or anonymize it.
              </p>

              <h2>Your Rights</h2>
              <p>Under Australian privacy law, you have the right to:</p>
              <ul>
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate or incomplete information</li>
                <li>Request deletion of your personal information</li>
                <li>Object to or restrict certain types of processing</li>
                <li>Withdraw consent where we rely on consent to process your information</li>
                <li>Lodge a complaint with the Office of the Australian Information Commissioner (OAIC)</li>
              </ul>
              <p>
                To exercise these rights, please contact us using the information provided below.
              </p>

              <h2>Cookies and Tracking Technologies</h2>
              <p>
                We use cookies and similar tracking technologies to track activity on our website and hold certain information. Cookies are files with small amounts of data that are sent to your browser from a website and stored on your device. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
              </p>

              <h2>Third Party Links</h2>
              <p>
                Our website may contain links to third party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third party sites you visit.
              </p>

              <h2>Children's Privacy</h2>
              <p>
                Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you become aware that a child has provided us with personal information, please contact us.
              </p>

              <h2>Changes to This Privacy Policy</h2>
              <p>
                We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. You are advised to review this Privacy Policy periodically for any changes.
              </p>

              <h2>Contact Us</h2>
              <p>If you have any questions about this Privacy Policy, please contact us:</p>
              <ul>
                <li><strong>Phone:</strong> 0413 24 55 19</li>
                <li><strong>Email:</strong> info@aussieinspections.com.au</li>
                <li><strong>Address:</strong> Darwin, Northern Territory, Australia</li>
              </ul>

              <h2>Compliance</h2>
              <p>
                This Privacy Policy is designed to comply with the Australian Privacy Principles (APPs) contained in the Privacy Act 1988 (Cth). We are committed to handling personal information in accordance with these principles.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
